import type { CSSProperties, FC } from 'react';
import type { DoctoralCabinetCard, DoctoralSummaryData } from '../types';
import {
  AwardIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CalendarIcon,
  FileTextIcon,
  GlobeIcon,
  ShieldCheckIcon,
  UserIcon,
} from './DoctoralIcons';

interface DoctoralCabinetGridProps {
  summary: DoctoralSummaryData;
  onOpenCard: (card: DoctoralCabinetCard) => void;
  onOpenModal: (modalId: string, payload?: unknown) => void;
}

const ACCENTS = [
  '#41d2ff',
  '#49ddb7',
  '#5da8ff',
  '#9e80ff',
  '#e2ad3a',
  '#66b7e8',
  '#ff7a64',
  '#41d2ff',
  '#49ddb7',
  '#5da8ff',
  '#9e80ff',
  '#e2ad3a',
  '#66b7e8',
  '#ff7a64',
];

export const DoctoralCabinetGrid: FC<DoctoralCabinetGridProps> = ({
  summary,
  onOpenCard,
  onOpenModal,
}) => {
  const { profile, kpis, cards } = summary;
  const initials =
    profile.full_name
      ?.split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase() || 'ДР';

  const renderCardIcon = (iconName: string) => {
    switch (iconName) {
      case 'academic_cap':
      case 'book':
        return <BookOpenIcon className="w-5 h-5" />;
      case 'file_text':
      case 'document':
        return <FileTextIcon className="w-5 h-5" />;
      case 'user':
      case 'users':
        return <UserIcon className="w-5 h-5" />;
      case 'calendar':
        return <CalendarIcon className="w-5 h-5" />;
      case 'award':
        return <AwardIcon className="w-5 h-5" />;
      case 'globe':
        return <GlobeIcon className="w-5 h-5" />;
      case 'briefcase':
        return <BriefcaseIcon className="w-5 h-5" />;
      case 'shield':
        return <ShieldCheckIcon className="w-5 h-5" />;
      default:
        return <FileTextIcon className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="role-hero" style={{ '--role-accent': '#41d2ff' } as CSSProperties}>
        <div className="flex items-start sm:items-center gap-4 min-w-0">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-tr from-cyan-500 to-emerald-400 flex items-center justify-center text-slate-950 font-black text-lg shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <small>
              {profile.academia_id} · Докторант
              {profile.role_subtitle &&
              !/предпросмотр|preview|докторант|learner/i.test(profile.role_subtitle)
                ? ` · ${profile.role_subtitle}`
                : profile.role_subtitle && /предпросмотр|preview/i.test(profile.role_subtitle)
                  ? ' · Предпросмотр владельца'
                  : ''}
            </small>
            <h2 className="truncate">{profile.full_name}</h2>
            <p>
              {profile.program_title} · {profile.group_name} · {profile.year_semester}
            </p>
          </div>
        </div>
        <div className="role-hero-actions">
          <button type="button" className="role-btn" onClick={() => onOpenModal('super_ai')}>
            Super AI
          </button>
          <button type="button" className="role-btn" onClick={() => onOpenModal('history')}>
            История
          </button>
          <button type="button" className="role-btn" onClick={() => onOpenModal('problem_report')}>
            Справка
          </button>
          <button
            type="button"
            className="role-btn primary"
            onClick={() => onOpenModal('create_request')}
          >
            + Создать заявку
          </button>
        </div>
      </div>

      <div className="role-statgrid">
        <div className="role-stat">
          <strong>{kpis.plan_percentage}%</strong>
          <span>Выполнение ИУП</span>
        </div>
        <div className="role-stat">
          <strong>{kpis.tasks_count}</strong>
          <span>Задач в работе</span>
        </div>
        <div className="role-stat">
          <strong>{kpis.publications_count}</strong>
          <span>Публикаций</span>
        </div>
        <div className="role-stat">
          <strong>{kpis.deadlines_count}</strong>
          <span>Дедлайнов</span>
        </div>
      </div>

      <div>
        <div className="mb-4">
          <h2 className="text-lg font-bold text-white tracking-tight">
            Рабочий кабинет докторанта
          </h2>
          <p className="text-xs text-slate-400">14 модулей кабинета докторанта</p>
        </div>

        <div className="owner-hub-grid">
          {cards.map((card, idx) => (
            <button
              key={card.num}
              type="button"
              onClick={() => onOpenCard(card)}
              className="owner-hub-card"
              style={{ '--action': ACCENTS[idx % ACCENTS.length] } as CSSProperties}
            >
              <span className="owner-hub-index">{card.num}</span>
              <span className="owner-hub-icon" aria-hidden="true">
                {renderCardIcon(card.icon)}
              </span>
              <span className="owner-hub-copy">
                <strong>{card.title}</strong>
                <span>
                  {card.desc}
                  {card.badge ? ` · ${card.badge}` : ''}
                </span>
              </span>
              <span className="owner-hub-arrow" aria-hidden="true">
                ›
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
